import React, { useRef, useState } from 'react';
import update from 'immutability-helper';
import PropTypes from 'prop-types';
import MixPanel from 'utils/mixpanelService';
import _get from 'lodash/get';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { setNPSVisibility } from 'containers/Main/actions';
import { useFirebase } from 'react-redux-firebase';
import { getLocalData, setLocalData } from 'utils/localDataStore';
import { useDispatch, useSelector } from 'react-redux';
import { makeSelectProfile } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import Divider from '@material-ui/core/Divider';
import withSizes from 'react-sizes';
import ReactTooltip from 'react-tooltip';
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  PinterestShareButton,
} from 'react-share';
import { DEFAULT_CONFIG } from 'components/NPSSurvey/constants';
import ClassNames from 'classnames';
import {
  fallbackImage,
  getParentShareUrl,
  generateShareUrl,
} from 'utils/stringUtils';
import LinkIcon from '@material-ui/icons/Link';
import ButtonBase from '@material-ui/core/ButtonBase';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ShareTransparentIcon from './images/share-transparent.svg';
import ShareIcon from './images/share.svg';
import FBIcon from './images/facebook.svg';
import TwitterIcon from './images/twitter.svg';
import EmailIcon from './images/email.svg';
import LinkedInIcon from './images/linkedin.svg';
import PinterestIcon from './images/pinterest.svg';

const useStyles = makeStyles(theme => ({
  paper: {
    boxShadow: 'none',
    borderRadius: '3px !important',
    border: '1px solid #E3E3E3',
  },
  withShadow: {
    boxShadow:
      '0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)',
  },
  buttonRoot: {
    color: theme.palette.text.primary,
    padding: 0,
    maxWidth: '100%',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  isButton: {
    width: '100%',
  },
  popperContainer: {
    zIndex: 999,
  },
  inverseText: {
    color: theme.palette.text.primary,
  },
  shareIcon: {
    width: 20,
    height: 20,
  },
  label: {
    fontSize: 12,
    lineHeight: 1.5,
    marginLeft: 8,
  },
  divider: {
    margin: '0 8%',
  },
  listItem: {
    '& > div': {
      display: 'flex',
    },
  },
  buttonWrapper: {
    width: '100%',
    border: '1px solid #E3E3E3',
    borderRadius: 5,
    height: 48,
    display: 'flex',
  },
  smallBtn: {
    width: 39,
    height: 39,
  },
  bigBtn: {
    width: 90,
    [theme.breakpoints.down('md')]: {
      width: 70,
    },
  },
  whiteBtn: {
    background: '#ffff',
  },
  linkIcon: {
    fontSize: 20,
    marginRight: 5,
  },
  linkBtn: {
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily: 'MadaRegular',
    color: 'rgb(75, 75, 75)',
    '& input': {
      position: 'absolute',
      top: '-999999999px',
    },
  },
}));

const Share = ({
  id,
  name,
  mobileShare,
  isAdmin,
  iconOnly,
  inverseText,
  type,
  withLinkedin,
  withPinterest,
  isButton,
  isBig,
  customIcon,
  url,
  copyLinkSource = 'link',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const linkRef = useRef();
  const classes = useStyles();
  const dispatch = useDispatch();
  const profile = useSelector(makeSelectProfile());
  const clientDetails = useSelector(makeSelectClientDetails());
  const firebase = useFirebase();

  const [siteCopy, resourceItemPageSiteCopy] = useSiteCopySelector([
    'nps',
    'resource-item-page',
  ]);
  const npsSiteCopy = _get(siteCopy, 'pageCopy', {});
  const npsClient = _get(clientDetails, 'metadata.nps', {});
  const finalNPS = update(npsSiteCopy, {
    $merge: npsClient,
  });

  const shareCount = _get(
    finalNPS,
    'config.shareCount',
    DEFAULT_CONFIG.shareCount,
  );
  const daysForRetake = _get(
    finalNPS,
    'config.daysForRetake',
    DEFAULT_CONFIG.daysForRetake,
  );

  const handleClick = social => {
    MixPanel.track(`Sharing Resource`, {
      name,
      type,
      social,
      entryId: id,
    });

    let npsShareCount;
    if (!profile.isEmpty) {
      npsShareCount = (profile.npsShareCount || 0) + 1;
      firebase.updateProfile({
        npsShareCount,
      });
    } else {
      npsShareCount = getLocalData('npsShareCount') || 0;
      setLocalData('npsShareCount', npsShareCount + 1);
    }
    if (npsShareCount + 1 > shareCount) {
      const lastNPS = profile.isEmpty
        ? getLocalData('npsDate')
        : _get(profile, 'npsDate', 0);
      if (
        !_get(profile, 'email', '').includes('@crediblemind.com') &&
        Date.now() > lastNPS + daysForRetake * 24 * 60 * 60 * 1000
      )
        dispatch(
          setNPSVisibility({
            visiblity: true,
            reason: 'share',
          }),
        );
    }
  };

  const getLabel = label => (
    <Typography className={classes.label}>{label}</Typography>
  );
  const handleBtnClick = event => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const getIconBtnContent = () => (
    <IconButton
      ref={anchorEl}
      aria-owns={open ? 'menu-list-grow' : undefined}
      aria-haspopup="true"
      className={ClassNames(
        classes.buttonRoot,
        inverseText ? classes.inverseText : undefined,
        isButton ? classes.isButton : undefined,
      )}
      disableRipple
      data-tip
      data-for={id}
      aria-label="Share"
      onClick={handleBtnClick}
    >
      {customIcon || (
        <img
          src={
            (mobileShare || isAdmin) && !iconOnly && !inverseText
              ? ShareTransparentIcon
              : ShareIcon
          }
          className={classes.shareIcon}
          alt="Share"
        />
      )}
      {!mobileShare && !iconOnly && !isAdmin && (
        <Typography variant="overline">
          {_get(resourceItemPageSiteCopy, 'pageCopy.shareResourceLabel')}
        </Typography>
      )}
    </IconButton>
  );

  const getIconBtn = () => {
    const btnContent = getIconBtnContent();
    const isBestResource = window.location.pathname.includes('/bestlists/');
    const isTopic = type === 'topic';
    if (mobileShare || isAdmin || iconOnly) {
      return (
        <div
          className={ClassNames(
            isButton && classes.buttonWrapper,
            isBestResource && classes.whiteBtn,
            isTopic && classes.smallBtn,
            isBig && classes.bigBtn,
          )}
        >
          {!mobileShare && (
            <ReactTooltip id={id} place="top" type="light" border>
              {_get(resourceItemPageSiteCopy, 'pageCopy.shareResourceLabel')}
            </ReactTooltip>
          )}

          {btnContent}
        </div>
      );
    }
    return btnContent;
  };

  const handleCopyLink = () => {
    if (linkRef.current) linkRef.current.select();
    document.execCommand('copy');
  };

  const parentHref = getParentShareUrl();
  const shareUrl = url || parentHref || window.location.href;
  const shareQuote = `${name} @CredibleMind`;
  const image = fallbackImage; // default for now since no image for editor lsit
  const open = Boolean(anchorEl);
  return (
    <>
      {getIconBtn()}
      <Popper
        open={open}
        anchorEl={anchorEl}
        className={classes.popperContainer}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="menu-list-grow"
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper
              className={ClassNames(
                classes.paper,
                inverseText ? classes.withShadow : undefined,
              )}
            >
              <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                <MenuList>
                  <MenuItem className={classes.listItem}>
                    <FacebookShareButton
                      url={generateShareUrl(shareUrl, 'facebook')}
                      quote={shareQuote}
                      className={classes.icon}
                      beforeOnClick={() => handleClick('facebook')}
                    >
                      <img src={FBIcon} alt="facebook" />
                      {getLabel(
                        _get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareFacebookLabel',
                        ),
                      )}
                    </FacebookShareButton>
                  </MenuItem>
                  <Divider className={classes.divider} />
                  <MenuItem className={classes.listItem}>
                    <TwitterShareButton
                      url={generateShareUrl(shareUrl, 'twitter')}
                      title={shareQuote}
                      className={classes.icon}
                      beforeOnClick={() => handleClick('twitter')}
                    >
                      <img src={TwitterIcon} alt="twitter" />
                      {getLabel(
                        _get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareTwitterLabel',
                        ),
                      )}
                    </TwitterShareButton>
                  </MenuItem>
                  <Divider className={classes.divider} />
                  <MenuItem className={classes.listItem}>
                    <EmailShareButton
                      url={generateShareUrl(shareUrl, 'email')}
                      subject={shareQuote}
                      className={classes.icon}
                      beforeOnClick={() => handleClick('email')}
                    >
                      <img src={EmailIcon} alt="email" />
                      {getLabel(
                        _get(
                          resourceItemPageSiteCopy,
                          'pageCopy.shareEmailLabel',
                        ),
                      )}
                    </EmailShareButton>
                  </MenuItem>
                  {withLinkedin && (
                    <>
                      <Divider className={classes.divider} />
                      <MenuItem className={classes.listItem}>
                        <LinkedinShareButton
                          url={generateShareUrl(shareUrl, 'linkedin')}
                          title={shareQuote}
                          beforeOnClick={() => handleClick('linkedin')}
                        >
                          <img src={LinkedInIcon} alt="linkedin" />
                          {getLabel(
                            _get(
                              resourceItemPageSiteCopy,
                              'pageCopy.shareLinkedInLabel',
                            ),
                          )}
                        </LinkedinShareButton>
                      </MenuItem>
                    </>
                  )}
                  {withPinterest && (
                    <>
                      <Divider className={classes.divider} />
                      <MenuItem className={classes.listItem}>
                        <PinterestShareButton
                          url={generateShareUrl(
                            String(window.location),
                            'pinterest',
                          )}
                          media={image}
                          beforeOnClick={() => handleClick('pinterest')}
                        >
                          <img src={PinterestIcon} alt="pinterest" />
                          {getLabel(
                            _get(
                              resourceItemPageSiteCopy,
                              'pageCopy.sharePinterestLabel',
                            ),
                          )}
                        </PinterestShareButton>
                      </MenuItem>
                    </>
                  )}
                  <Divider className={classes.divider} />
                  <MenuItem className={classes.listItem}>
                    <ButtonBase
                      className={classes.linkBtn}
                      onClick={handleCopyLink}
                    >
                      <input
                        type="text"
                        className={classes.hiddenInput}
                        ref={linkRef}
                        value={generateShareUrl(shareUrl, copyLinkSource)}
                      />
                      <LinkIcon className={classes.linkIcon} />
                      {_get(
                        resourceItemPageSiteCopy,
                        'pageCopy.shareLinkLabel',
                      )}
                    </ButtonBase>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

Share.propTypes = {
  name: PropTypes.string,
  mobileShare: PropTypes.bool,
  isAdmin: PropTypes.bool,
  iconOnly: PropTypes.bool,
  inverseText: PropTypes.bool,
  type: PropTypes.string,
  withLinkedin: PropTypes.bool,
  withPinterest: PropTypes.bool,
  isButton: PropTypes.bool,
  isBig: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  mobileShare: width < 750,
});

export default withSizes(mapSizesToProps)(Share);
